import { useState, useEffect } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { BudgetV2Dto } from '@cloudcost/shared-types/budgets';
import { urls } from '@/urls';
import { UserDto } from '@cloudcost/shared-types/users';

export interface Department {
  id: number;
  name: string;
  username: string;
  value: number;
  id_owner: number;
  original_currency_value?: string;
  original_currency?: string;
  budget_id?: number;
}

type BudgetsBulkResponse = {
  departments: Array<{ departmentId: number; budget: BudgetV2Dto | null }>;
};

export interface ChartItem {
  name: string;
  spending: number;
  value: number;
}

export const useDepartments = () => {
  const [departmentsList, setDepartmentsList] = useState<Department[]>([]);
  const [chartItems, setChartItems] = useState<ChartItem[]>([]);
  const [users, setUsers] = useState<UserDto[]>([]);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchDepartmentsData = async () => {
    setLoading(true);
    try {
      const [usersData, departmentsData, currenciesData, chartData] =
        await Promise.all([
          fetchWrapper.get(urls.DevOps.getUsersBusiness),
          fetchWrapper.get(urls.DevOps.getDepartments),
          fetchWrapper.get(urls.DevOps.currencies),
          fetchWrapper.get(urls.DevOps.spendingByDepartments),
        ]);

      setUsers(usersData);
      // Fetch budgets via v2 GET endpoints and map into existing model fields
      const baseDepartments = departmentsData as Department[];

      const budgetByDepartmentId = new Map<number, BudgetV2Dto>();
      try {
        const bulk = (await fetchWrapper.post(urls.DevOps.getLatestBudgetsV2Bulk, {
          departmentIds: baseDepartments.map((d) => d.id),
        })) as BudgetsBulkResponse;

        (bulk.departments ?? [])
          .filter((x) => x.budget)
          .forEach((x) => {
            budgetByDepartmentId.set(x.departmentId, x.budget as BudgetV2Dto);
          });
      } catch (e) {
        console.warn('[useDepartments] Failed to fetch budgets bulk, falling back to per-department calls', e);
        const budgetResults = await Promise.all(
          baseDepartments.map(async (d) => {
            try {
              const budget = (await fetchWrapper.get(
                urls.DevOps.getLatestBudgetV2ForScope({ departmentId: d.id }),
              )) as BudgetV2Dto;
              return { departmentId: d.id, budget };
            } catch {
              return { departmentId: d.id, budget: null as BudgetV2Dto | null };
            }
          }),
        );
        budgetResults
          .filter((r) => r.budget)
          .forEach((r) => budgetByDepartmentId.set(r.departmentId, r.budget!));
      }

      const enrichedDepartments: Department[] = baseDepartments.map((d) => {
        const b = budgetByDepartmentId.get(d.id);
        if (!b) return d;
        return {
          ...d,
          value: Number(b.yearly_limit),
          original_currency_value: String(b.yearly_limit),
          original_currency: b.currency,
          budget_id: b.budget_id,
        };
      });

      setDepartmentsList(enrichedDepartments);
      setCurrencies(currenciesData.map((x: any) => x.currency));
      setChartItems(chartData);
    } catch (error) {
      console.error('Error fetching departments data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartmentsData();
  }, []);

  const refreshData = () => {
    return fetchDepartmentsData();
  };

  return {
    departmentsList,
    chartItems,
    users,
    currencies,
    loading,
    refreshData,
    setDepartmentsList,
    setChartItems,
  };
};
